import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, ExternalLink, Crown, Shield, Receipt, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Subscription {
  tier: string;
  status: string;
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
}

interface PaymentRecord {
  id: string;
  amount_cents: number;
  currency: string;
  status: string;
  description: string;
  created_at: string;
}

const tierLabels: Record<string, { label: string; color: string; icon: typeof Crown }> = {
  free: { label: "Free", color: "text-muted-foreground", icon: Shield },
  pro: { label: "Pro", color: "text-primary", icon: Crown },
  premium: { label: "Pro", color: "text-primary", icon: Crown },
  business: { label: "Business", color: "text-amber-600", icon: Crown },
  admin: { label: "Admin", color: "text-red-600", icon: Shield },
};

const statusLabels: Record<string, { label: string; color: string }> = {
  none: { label: "No subscription", color: "text-muted-foreground" },
  active: { label: "Active", color: "text-emerald-600" },
  trialing: { label: "Trial", color: "text-sky-600" },
  past_due: { label: "Past Due", color: "text-red-600" },
  canceled: { label: "Canceled", color: "text-amber-600" },
};

export default function BillingPage() {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(window.location.search);
  const success = searchParams.get("success") === "true";

  useEffect(() => {
    loadBillingData();
  }, []);

  async function loadBillingData() {
    try {
      const [subRes, histRes] = await Promise.all([
        api.get("/billing/subscription"),
        api.get("/billing/history"),
      ]);
      setSubscription(subRes.data);
      setPayments(histRes.data);
    } catch (err) {
      console.error("Failed to load billing data:", err);
    } finally {
      setLoading(false);
    }
  }

  async function openPortal() {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal");
      window.location.href = res.data.url;
    } catch (err: any) {
      console.error("Portal error:", err);
      alert(err.response?.data?.error || "Failed to open billing portal");
      setPortalLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading billing...</div>
      </div>
    );
  }

  const tierInfo = tierLabels[subscription?.tier || "free"] || tierLabels.free;
  const statusInfo = statusLabels[subscription?.status || "none"] || statusLabels.none;
  const TierIcon = tierInfo.icon;
  const isPaid = subscription?.tier !== "free" && subscription?.status === "active";

  return (
    <div className="space-y-6">
      {success && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-700 flex items-center gap-2">
          <Crown className="h-5 w-5 text-emerald-600" />
          <span>
            <strong>Welcome to TripMuscle {tierInfo.label}!</strong> Your subscription is now active.
            Enjoy unlimited searches and all premium features.
          </span>
        </div>
      )}

      {/* Current Plan Card */}
      <Card className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <TierIcon className={cn("h-5 w-5", tierInfo.color)} />
              {tierInfo.label} Plan
            </h2>
            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              <p>
                Status:{" "}
                <span className={cn("font-medium", statusInfo.color)}>
                  {statusInfo.label}
                </span>
              </p>
              {subscription?.periodEnd && isPaid && (
                <p>
                  {subscription.cancelAtPeriodEnd ? "Expires" : "Renews"}:{" "}
                  {new Date(subscription.periodEnd).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              )}
              {subscription?.cancelAtPeriodEnd && (
                <p className="text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Canceling at end of billing period
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {isPaid && (
              <Button variant="outline" size="sm" onClick={openPortal} disabled={portalLoading}>
                <CreditCard className="h-4 w-4 mr-2" />
                {portalLoading ? "Opening..." : "Manage"}
                <ExternalLink className="h-3 w-3 ml-1.5" />
              </Button>
            )}
            {subscription?.tier === "free" && (
              <Button size="sm" onClick={() => navigate("/pricing")}>
                <Crown className="h-4 w-4 mr-2" />
                Upgrade
              </Button>
            )}
            {subscription?.tier === "pro" && subscription?.status === "active" && (
              <Button variant="outline" size="sm" onClick={() => navigate("/pricing")}>
                Upgrade to Business
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Payment History */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <Receipt className="h-5 w-5 text-muted-foreground" />
          Payment History
        </h2>

        {payments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No payments yet.{" "}
            {subscription?.tier === "free" && (
              <button onClick={() => navigate("/pricing")} className="text-primary hover:underline">
                Upgrade to Pro
              </button>
            )}
          </p>
        ) : (
          <div className="space-y-2">
            {payments.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between py-3 border-b border-border last:border-0"
              >
                <div>
                  <p className="text-sm font-medium">{payment.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(payment.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    ${(payment.amount_cents / 100).toFixed(2)}
                  </p>
                  <p
                    className={cn(
                      "text-xs",
                      payment.status === "succeeded" ? "text-emerald-600" : "text-red-600"
                    )}
                  >
                    {payment.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
